import { useState } from "react";
import { adminApi, type FlagWithContext } from "@/lib/admin-api";

interface Props {
  flag: FlagWithContext;
  onClose: () => void;
  onResolved: (id: number) => void;
  onAlreadyResolved: () => void;
}

export function FlagDetail({ flag, onClose, onResolved, onAlreadyResolved }: Props) {
  const [pendingAction, setPendingAction] = useState<"approve" | "takedown" | null>(null);
  const [takedownConfirm, setTakedownConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const reporterLabel = flag.reporter_email ?? "auto:safety_filter";
  const createdDate = new Date(flag.created_at).toISOString().slice(0, 10);

  async function handleAction(action: "approve" | "takedown") {
    if (action === "takedown" && !takedownConfirm) {
      setTakedownConfirm(true);
      return;
    }
    setBusy(true);
    try {
      await adminApi.resolveFlag(flag.id, action);
      onResolved(flag.id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("409") || msg.includes("flag_already_resolved")) {
        onAlreadyResolved();
      }
    } finally {
      setBusy(false);
    }
  }

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="flag-detail-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="flag-detail-title"
      data-testid="flag-detail"
      onClick={handleOverlayClick}
    >
      <div className="flag-detail-panel">
        <div className="flag-detail-header">
          <div className="flag-detail-header__meta">
            <span className="flag-detail-header__eyebrow">FLAG REVIEW</span>
            <span className="flag-detail-header__date">{createdDate}</span>
          </div>
          <button
            className="flag-detail-header__close"
            onClick={onClose}
            aria-label="Close flag detail"
            data-testid="flag-detail-close"
          >
            ×
          </button>
        </div>

        <div className="flag-detail-body">
          <section className="flag-detail-section">
            <h2 id="flag-detail-title" className="flag-detail-section__label">
              ARTIFACT
            </h2>
            <p className="flag-detail-artifact-title" data-testid="flag-artifact-title">
              {flag.artifact_title}
            </p>
            <pre className="flag-detail-artifact-body" data-testid="flag-artifact-body">
              {flag.artifact_body}
            </pre>
          </section>

          <section className="flag-detail-section">
            <h3 className="flag-detail-section__label">FLAG CONTEXT</h3>
            <dl className="flag-detail-meta-list">
              <dt>Automaton</dt>
              <dd data-testid="flag-automaton-handle">
                {flag.automaton_handle}
                <span className="flag-detail-status">{flag.automaton_status}</span>
              </dd>
              <dt>Reporter</dt>
              <dd data-testid="flag-reporter">{reporterLabel}</dd>
              <dt>Reason</dt>
              <dd data-testid="flag-reason">{flag.reason}</dd>
              {flag.details && (
                <>
                  <dt>Details</dt>
                  <dd data-testid="flag-details">{flag.details}</dd>
                </>
              )}
            </dl>
          </section>
        </div>

        <div className="flag-detail-actions">
          <button
            className="flag-detail-btn flag-detail-btn--approve"
            disabled={busy || pendingAction !== null}
            onClick={() => {
              setPendingAction("approve");
              void handleAction("approve");
            }}
            data-testid="flag-approve-btn"
          >
            {pendingAction === "approve" && busy ? "APPROVING…" : "APPROVE"}
          </button>

          <button
            className={
              "flag-detail-btn flag-detail-btn--takedown" +
              (takedownConfirm ? " flag-detail-btn--takedown-confirm" : "")
            }
            disabled={busy || pendingAction === "approve"}
            onClick={() => {
              setPendingAction("takedown");
              void handleAction("takedown");
            }}
            data-testid="flag-takedown-btn"
          >
            {takedownConfirm
              ? pendingAction === "takedown" && busy
                ? "TAKING DOWN…"
                : "CONFIRM TAKEDOWN"
              : "TAKEDOWN"}
          </button>
        </div>
      </div>
    </div>
  );
}
